import React from "react";
import { Link } from "react-router-dom";
import { Camera, TrendingUp, Key, Sparkles, Calculator, HeadphonesIcon, ArrowRight, Check } from "lucide-react";
import { useI18n } from "../i18n";
import { imgProxy, imgThumb } from "../lib/imgProxy";

const icons = [Camera, TrendingUp, Key, Sparkles, Calculator, HeadphonesIcon];

export default function ServicesPage() {
  const { t } = useI18n();
  return (
    <main>
      <section className="relative h-[48vh] min-h-[380px] flex items-center hero-bg">
        <div className="hero-img" style={{ backgroundImage: `url('${imgProxy("https://images.unsplash.com/photo-1520637836862-4d197d17c55a", { w: 2000, q: 78 })}')` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 text-white">
          <p className="text-[11px] uppercase tracking-[.3em] opacity-90 mb-4">MyHost</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight" data-testid="services-title">{t.services.title}</h1>
          <p className="mt-4 opacity-90 max-w-xl">{t.services.subtitle}</p>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8">
        <p className="font-serif text-2xl sm:text-3xl italic leading-relaxed max-w-3xl mb-16 text-[hsl(var(--ink-soft))]">
          « {t.services.intro} »
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.items.map((s, i) => {
            const Icon = icons[i] || Sparkles;
            return (
              <div key={i} className="group p-8 bg-white rounded-xl border border-[hsl(var(--line))] hover:border-[hsl(var(--gold))] transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`service-item-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--bg-warm))] flex items-center justify-center mb-5 group-hover:bg-[hsl(var(--gold))] transition-colors">
                  <Icon className="w-5 h-5 text-[hsl(var(--gold-deep))] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-xl mb-3">{s.t}</h3>
                <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-20 text-center">
          <Link to="/louer-votre-bien" className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold" data-testid="services-cta">
            {t.home.ctaBtn} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Check-in / Check-out à la carte */}
      <section className="py-24 bg-[hsl(var(--bg-warm))] border-y border-[hsl(var(--line))]" data-testid="checkin-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative aspect-[4/3] rounded-2xl overflow-hidden order-last lg:order-first">
            <img src={imgThumb("https://customer-assets.emergentagent.com/job_chamonix-locations/artifacts/r4vyra0d_Chamonix_img.jpg")}
                 alt="Check-in MyHost" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--ink))]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 text-white">
              <Key className="w-6 h-6 text-[hsl(var(--gold))]" />
              <span className="font-serif text-xl">Disponible 7j/7</span>
            </div>
          </div>

          <div>
            <span className="inline-block text-[10px] uppercase tracking-[.25em] font-semibold text-[hsl(var(--gold-deep))] bg-[hsl(var(--gold))]/10 px-3 py-1.5 rounded-full mb-5" data-testid="checkin-tag">
              {t.services.checkinTag}
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-5">{t.services.checkinTitle}</h2>
            <p className="font-serif text-xl italic text-[hsl(var(--gold-deep))] mb-5 leading-snug">« {t.services.checkinHook} »</p>
            <p className="text-[hsl(var(--ink-soft))] leading-relaxed mb-8">{t.services.checkinBody}</p>

            <ul className="space-y-3 mb-10">
              {t.services.checkinPoints.map((p, i) => (
                <li key={i} className="flex gap-3 items-start text-sm" data-testid={`checkin-point-${i}`}>
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--gold))] flex items-center justify-center mt-0.5">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-[hsl(var(--ink))]">{p}</span>
                </li>
              ))}
            </ul>

            <Link to="/contact" className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold" data-testid="checkin-cta">
              {t.services.checkinCta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
